import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, AlertTriangle } from "lucide-react";

const TYPE_LABEL = {
  equity_research: "Equity research",
  private_credit_memo: "Private credit memo",
  pe_investment_memo: "PE / VC investment memo",
  quarterly_portfolio_update: "Quarterly portfolio update",
};

export default function ReportView() {
  const { id } = useParams();
  const [report, setReport] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/reports/${id}`);
      setReport(r.data);
    } catch (e) {
      toast.error("Could not load report");
    }
  };

  useEffect(() => {
    load();
    const tid = setInterval(() => {
      setReport((prev) => {
        if (prev && prev.status === "completed") return prev;
        load();
        return prev;
      });
    }, 4000);
    return () => clearInterval(tid);
    // eslint-disable-next-line
  }, [id]);

  const exportMd = () => {
    const blob = new Blob([report?.content || ""], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${report.company_name}-${report.report_type}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!report) {
    return (
      <div className="max-w-[900px] mx-auto px-10 py-12" data-testid="report-view-loading">
        <div className="text-[var(--warm-grey)] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-10 py-12" data-testid="report-view-page">
      <Link to="/app/reports" className="text-[12px] tracking-[0.18em] uppercase font-semibold text-[var(--warm-grey)] hover:text-[var(--ink)] inline-flex items-center gap-2 transition-colors" data-testid="report-back-link">
        <ArrowLeft className="w-4 h-4" /> All reports
      </Link>

      {/* Masthead */}
      <div className="mt-8 border-y border-[var(--ink)] py-6">
        <div className="flex items-baseline justify-between text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--warm-grey)]">
          <span>cosalpha · {TYPE_LABEL[report.report_type]}</span>
          <span>{new Date(report.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</span>
        </div>
        <div className="flex items-end justify-between mt-5">
          <div>
            <div className="smallcaps text-[var(--saffron)]">{TYPE_LABEL[report.report_type]}</div>
            <h1 className="font-serif text-[42px] font-medium tracking-[-0.02em] mt-3" data-testid="report-company-name">
              {report.company_name} {report.ticker && <span className="text-[var(--warm-grey)] font-mono text-[20px] ml-1">({report.ticker})</span>}
            </h1>
            {report.sector && <div className="text-[13px] text-[var(--warm-grey)] mt-1 italic font-serif">{report.sector}</div>}
          </div>
          {report.status === "completed" && (
            <button onClick={exportMd} className="inline-flex items-center gap-2 bg-white border border-[var(--ink)]/30 hover:border-[var(--ink)] px-4 py-2.5 text-[12px] tracking-wide transition-colors" data-testid="report-export-button">
              <Download className="w-3.5 h-3.5" /> Export .md
            </button>
          )}
        </div>
      </div>

      <div className="mt-10 bg-white border border-[var(--ink)]/15 p-10 lg:p-14 min-h-[400px]">
        {report.status === "generating" && (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="report-generating">
            <Loader2 className="w-8 h-8 text-[var(--saffron)] animate-spin" strokeWidth={1.5} />
            <div className="mt-8 font-serif text-[28px] font-medium">Drafting your memo…</div>
            <p className="text-[14px] text-[var(--ink)]/75 mt-3 max-w-md">Reviewing source documents, normalising figures, and structuring the report. Typically 30–90 seconds.</p>
          </div>
        )}
        {report.status === "failed" && (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="report-failed">
            <AlertTriangle className="w-8 h-8 text-[#a33]" strokeWidth={1.5} />
            <div className="mt-8 font-serif text-[28px] font-medium">Generation failed</div>
            <p className="text-[14px] text-[var(--ink)]/75 mt-3 max-w-md">{report.error || "Something went wrong. Try again with different documents."}</p>
          </div>
        )}
        {report.status === "completed" && (
          <div data-testid="report-content">
            <Markdown text={report.content || ""} />
          </div>
        )}
      </div>
    </div>
  );
}
